import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface ComboboxOption {
  value: string
  label: string
  disabled?: boolean // For multi-select: option can be disabled
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Välj alternativ...",
  searchPlaceholder = "Sök...",
  emptyMessage = "Inget alternativ hittades.",
  className,
  disabled = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          <span className="truncate">
            {value
              ? options.find((option) => option.value === value)?.label
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onValueChange(option.value === value ? "" : option.value)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Multi-select Combobox variant
interface MultiSelectComboboxProps {
  options: ComboboxOption[]
  values: string[]
  onValuesChange: (values: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  showSelectAll?: boolean
  getDisplayText?: (selectedCount: number, totalCount: number) => string
}

export function MultiSelectCombobox({
  options,
  values,
  onValuesChange,
  placeholder = "Välj alternativ...",
  searchPlaceholder = "Sök...",
  emptyMessage = "Inget alternativ hittades.",
  className,
  disabled = false,
  showSelectAll = true,
  getDisplayText,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")

  const handleToggle = (optionValue: string) => {
    const newValues = values.includes(optionValue)
      ? values.filter(v => v !== optionValue)
      : [...values, optionValue]
    onValuesChange(newValues)
  }

  const handleSelectAll = () => {
    const selectableOptions = options.filter(opt => !opt.disabled)
    onValuesChange(selectableOptions.map(opt => opt.value))
  }

  const handleClearAll = () => {
    onValuesChange([])
  }

  const selectedCount = values.length
  const totalCount = options.length
  const displayText = selectedCount === 0
    ? placeholder
    : getDisplayText
      ? getDisplayText(selectedCount, totalCount)
      : `${selectedCount} valda`

  // Filter options based on search query for accurate count
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options

    const query = searchQuery.toLowerCase()
    return options.filter(opt =>
      opt.label.toLowerCase().includes(query) ||
      opt.value.toLowerCase().includes(query)
    )
  }, [options, searchQuery])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          <span className="truncate">
            {displayText}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)] p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {showSelectAll && !searchQuery && (
                <>
                  <CommandItem
                    onSelect={handleSelectAll}
                    className="font-medium"
                  >
                    Välj alla
                  </CommandItem>
                  <CommandItem
                    onSelect={handleClearAll}
                    className="font-medium"
                  >
                    Rensa alla
                  </CommandItem>
                  <div className="h-px bg-border my-1" />
                </>
              )}
              {filteredOptions.map((option) => {
                const isSelected = values.includes(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      if (!option.disabled) {
                        handleToggle(option.value)
                      }
                    }}
                    disabled={option.disabled}
                    className={cn(
                      option.disabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
          {/* Footer with item count */}
          <div className="border-t bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            {searchQuery ? (
              <span>Visar {filteredOptions.length} av {options.length} alternativ</span>
            ) : (
              <span>{options.length} alternativ totalt • Scrolla för fler</span>
            )}
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
